import { makeObservable, observable } from 'mobx';
import { IFilterDTOState, TFilterType } from '.';
import { FilterDTO } from './FilterDTO';
import { IFilterValue } from './types';

export class FiltersStore {
  private static instance: FiltersStore = new FiltersStore();

  available: Record<string, FilterDTO<IFilterDTOState>> = {};
  fixed: Record<string, FilterDTO<IFilterDTOState>> = {};
  dynamic: Record<string, FilterDTO<IFilterDTOState>> = {};

  private constructor() {
    makeObservable(this, {
      available: observable,
      fixed: observable,
      dynamic: observable,
    });
  }

  /**
   * Takes the string generated by the ParametersStore in the server
   * and assigns it to the current filters
   */
  public static getFiltersFromResponse(
    response: string,
    from: 'fixed' | 'dynamic' | 'available' = 'available',
  ) {
    const parsed = JSON.parse(response) as {
      available: Record<string, IFilterDTOState>;
      dynamic: Record<string, IFilterDTOState>;
      fixed: Record<string, IFilterDTOState>;
    };
    const newFilters: Record<string, FilterDTO> = {};

    Object.entries(parsed[from]).forEach(([name, param]) => {
      newFilters[name] = new FilterDTO(param);
    });
    return newFilters;
  }

  public static setFiltersFromObject(response: {
    available: Record<string, FilterDTO<IFilterDTOState>>;
    fixed: Record<string, FilterDTO<IFilterDTOState>>;
    dynamic: Record<string, FilterDTO<IFilterDTOState>>;
  }) {
    this.instance.available = this.getFiltersFromObject(response.available);
    this.instance.dynamic = this.getFiltersFromObject(response.dynamic);
    this.instance.fixed = this.getFiltersFromObject(response.fixed);
  }

  public static getFiltersFromObject(response: Record<string, FilterDTO>) {
    const newFilters: Record<string, FilterDTO> = {};

    Object.entries(response).forEach(([name, param]) => {
      if (!(param instanceof FilterDTO)) {
        param = new FilterDTO(param);
      }
      newFilters[name] = param;
    });
    return newFilters;
  }

  /**
   * Returns the filters for a given name.
   */
  static getFilter(name: string, type?: TFilterType) {
    return !type
      ? this.instance.available[name]
      : type === 'F'
        ? this.instance.fixed[name]
        : this.instance.dynamic[name];
  }

  /**
   * Set filter into store given owner name
   */
  static setFilter(
    value: FilterDTO<IFilterDTOState>,
    type?: TFilterType,
    replace = true,
  ) {
    const newFilter = new FilterDTO(value.state, type);
    if (!type) {
      this.instance.available[value.state.name] = newFilter;
    } else if (
      type === 'F' &&
      ((!replace && !this.instance.fixed[value.state.name]) || replace)
    ) {
      this.instance.fixed[value.state.name] = newFilter;
    } else if (
      type === 'U' &&
      ((!replace && !this.instance.dynamic[value.state.name]) || replace)
    ) {
      this.instance.dynamic[value.state.name] = newFilter;
    }
  }

  /**
   * Remove into store given owner name
   */
  static removeFilter(x: FilterDTO<IFilterDTOState>, type?: TFilterType) {
    if (!type) {
      delete this.instance.available[x.state.name];
      delete this.instance.fixed[x.state.name];
      delete this.instance.dynamic[x.state.name];
    } else if (type === 'U') {
      delete this.instance.dynamic[x.state.name];
      x.state.filterType = undefined as any as TFilterType;
    } else if (type === 'F') {
      delete this.instance.fixed[x.state.name];
      x.state.filterType = undefined as any as TFilterType;
    }
  }

  /**
   * Resets the parameters store.
   */
  static reset() {
    FiltersStore.instance.available = {};
    FiltersStore.instance.fixed = {};
    FiltersStore.instance.dynamic = {};
  }

  static size() {
    return Object.keys(FiltersStore.instance.available).length;
  }

  static setFilters(
    filters: Record<string, FilterDTO<IFilterDTOState>>,
    type?: TFilterType,
  ) {
    !type
      ? (this.instance.available = filters)
      : type === 'F'
        ? (this.instance.fixed = filters)
        : (this.instance.dynamic = filters);
  }

  static filters(type?: TFilterType) {
    return !type
      ? FiltersStore.instance.available
      : type === 'F'
        ? FiltersStore.instance.fixed
        : FiltersStore.instance.dynamic;
  }

  static parseFiltersAsFilterValue(values: FilterDTO[]) {
    return values.reduce((x: Record<string, IFilterValue>, y) => {
      const value = y.asFilterValue();
      if (value) {
        x[y.state.name] = value;
      }
      return x;
    }, {});
  }

  static getFiltersAsFilterValue(
    type?: TFilterType,
  ): Record<string, IFilterValue> {
    return FiltersStore.parseFiltersAsFilterValue(FiltersStore.values(type));
  }

  static getAllFilters(includeAvailable?: boolean) {
    const retList = [];
    includeAvailable &&
      retList.push(...Object.values(FiltersStore.instance.available));
    retList.push(...Object.values(FiltersStore.instance.fixed));
    retList.push(...Object.values(FiltersStore.instance.dynamic));
    return retList;
  }

  static values(type?: TFilterType) {
    return !type
      ? Object.values(FiltersStore.instance.available)
      : type === 'F'
        ? Object.values(FiltersStore.instance.fixed)
        : Object.values(FiltersStore.instance.dynamic);
  }

  static entries(type?: TFilterType) {
    return !type
      ? Object.entries(FiltersStore.instance.available)
      : type === 'F'
        ? Object.entries(FiltersStore.instance.fixed)
        : Object.entries(FiltersStore.instance.dynamic);
  }

  static keys(type?: TFilterType) {
    return !type
      ? Object.keys(FiltersStore.instance.available)
      : type === 'F'
        ? Object.keys(FiltersStore.instance.fixed)
        : Object.keys(FiltersStore.instance.dynamic);
  }

  static async getFiltersById(id: number) {
    const response = /* await ApiaApi.post<{
      parameters: string;
    }>(
      makeApiaUrl({
        ajaxUrl: 'apia.design.SemanticSearchAction.run',
        action: 'getFiltersBySemSerId',
        id,
      }),
    );*/ {} as any;

    const filters = this.getFiltersFromResponse(
      response?.data?.parameters ?? '',
      'dynamic',
    );
    Object.values(filters).forEach((x) => (x.isTd = false));
    return filters;
  }

  static asPayloadElement() {
    return FiltersStore.getAllFilters().map((x) => x.asPayloadElement());
  }

  static validate() {
    let isValid = true;
    let firstInvalid: FilterDTO | null = null;

    for (const f of FiltersStore.getAllFilters()) {
      isValid &&= f.validate();
      if (!isValid && !firstInvalid) {
        firstInvalid = f;
      }
    }

    firstInvalid?.ref?.focus();

    return isValid;
  }
}
